import { AxiosError, AxiosRequestConfig } from 'axios';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useNavigate } from 'react-router-dom';
import { httpClient, RouteParams } from '@/httpClient';
import { getSessionToken } from '@/utils/http';

interface AppQueryRequestConfig extends AxiosRequestConfig {
  routeParams?: RouteParams;
}

export type UseAppQueryOptions<TData = unknown> = Omit<
  UseQueryOptions<TData>,
  'queryKey'
>;
export type UseAppMutationOptions<TData = unknown, TVariables = unknown> = Omit<
  UseMutationOptions<TData, unknown, TVariables>,
  'mutationKey'
>;
export interface UseAppQueryProps<TData = unknown> {
  routeName: string;
  queryKey: string | unknown[];
  requestConfig?: AppQueryRequestConfig;
  options?: UseAppQueryOptions<TData>;
  method?: 'GET' | 'POST';
}

interface UseAppMutationProps<IResponse, TVariables = unknown> {
  routeName: string;
  mutationKey: string | unknown[];
  requestConfig?: AppQueryRequestConfig;
  options?: UseAppMutationOptions<IResponse, TVariables>;
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
}

/**
 * Custom hook to perform a query using the organization's HTTP client and react-query.
 *
 * @template IResponse - The expected response type from the query.
 * @param {UseAppQueryProps<IResponse>} props - The properties for configuring the query.
 * @param {string} props.routeName - The API route name to query.
 * @param {string | unknown[]} props.queryKey - Unique key for the query, used for caching.
 * @param {AppQueryRequestConfig} [props.requestConfig] - Optional Axios request configuration.
 * @param {UseAppQueryOptions<IResponse>} [props.options] - Optional react-query options.
 * @param {'GET' | 'POST'} [props.method='GET'] - **(Warning: Changing this is not recommended!)** HTTP method to use for the request.
 * @returns {UseQueryResult<IResponse>} The result of the query, including status and data.
 */
export function useAppQuery<IResponse = unknown>({
  routeName,
  queryKey,
  requestConfig,
  options,
  method = 'GET',
}: UseAppQueryProps<IResponse>): UseQueryResult<IResponse> {
  const navigate = useNavigate();

  return useQuery<IResponse>(
    queryKey,
    async () => {
      try {
        const token = await getSessionToken();
        const res = await httpClient.instance<IResponse>(routeName, {
          method,
          headers: {
            sessionToken: token?.data,
          },
          ...requestConfig,
        });
        return res.data;
      } catch (error) {
        if (error instanceof AxiosError && error?.response?.status === 401) {
          navigate('/');
        }
        throw error;
      }
    },
    options,
  );
}

export function useAppMutation<IResponse = unknown, TVariables = unknown>({
  routeName,
  mutationKey,
  requestConfig,
  options,
  method = 'POST',
}: UseAppMutationProps<IResponse, TVariables>) {
  return useMutation(
    mutationKey,
    async (value) => {
      const token = await getSessionToken();
      const res = await httpClient.instance<IResponse>(routeName, {
        method,
        headers: {
          sessionToken: token?.data,
        },
        data: value,
        ...requestConfig,
      });
      return res.data;
    },
    options,
  );
}
